<script setup lang="ts">
import { computed, h } from 'vue';

import type { ColumnDef, StatusTone } from '@/shared/ui';

import { LocationCodeBadge } from '@/entities/location/@x/payment';
import { formatMoney } from '@/shared/lib';
import { AppStatusChip, BaseTable } from '@/shared/ui';
import {
  type LocationColor,
  PaymentAttentionReason,
  PaymentJournalKind,
  PaymentJournalMoneyStatus,
  PaymentProvider,
  PaymentReceiptIssuer,
  PaymentReceiptStatus,
  ReceiptSettlementMethod,
} from '@shared/api/graphql/gql/graphql';
import { formatDate, formatDateTime, inTimezone } from '@shared/lib/date-time';

import type { PaymentJournalRow } from '../model/types';

import {
  getPaymentMoneyStatusTone,
  isSupersededRefundAttempt,
  paymentAttentionLabels,
  paymentJournalDateTime,
  paymentJournalFailureText,
  paymentJournalReceiptTone,
  paymentJournalShortStatusLabel,
  paymentMethodsLabel,
  paymentProviderLabels,
  paymentReceiptIssuerLabels,
  paymentReceiptStatusLabels,
} from '../lib/labels';

const props = defineProps<{
  rows: PaymentJournalRow[];
  empty: string;
  loading?: boolean;
  hasMore?: boolean;
  fetchingMore?: boolean;
  /** Журнал одного клиента: имя в каждой строке ничего не различает. */
  hideCustomer?: boolean;
  /** Журнал одной локации: столбец показывает только сотрудника. */
  hideLocation?: boolean;
  /** Код и цвет локаций: строка показывает плашку кода перед названием. */
  locationBadges?: ReadonlyMap<string, { code: string; color: LocationColor }>;
}>();
const emit = defineEmits<{ 'fetch-more': []; 'row-click': [row: PaymentJournalRow] }>();

// Эти причины уже видны плашкой под суммой или цветом чека.
const SHOWN_ATTENTION = new Set<PaymentAttentionReason>([
  PaymentAttentionReason.MoneyUnknown,
  PaymentAttentionReason.ReceiptFailed,
  PaymentAttentionReason.ReceiptUnknown,
  PaymentAttentionReason.RefundFailed,
  PaymentAttentionReason.RefundUnknown,
]);
const MUTED = 'text-(--text-secondary)';

const pill = (tone: StatusTone, label: string) => h(AppStatusChip, { tone, class: 'whitespace-nowrap' }, () => label);
const secondary = (text: string) => h('div', { class: `text-xs ${MUTED}` }, text);
// Текст кассы бывает длинным: в строке одна строка, целиком по наведению и в карточке.
const failureLine = (text: string | null) =>
  text ? h('div', { class: 'max-w-[240px] truncate text-xs text-(--danger)', title: text }, text) : null;
// Знак показывает направление денег: приход или возврат. Неразрывный пробел не даёт оторвать его от суммы.
const signedAmount = (row: PaymentJournalRow): string =>
  `${row.kind === PaymentJournalKind.Refund ? '−' : '+'}\u00A0${formatMoney(row.amount)}`;
const amountCell = (row: PaymentJournalRow) => {
  const status = row.money_status;
  const superseded = isSupersededRefundAttempt(row);
  const failed = status === PaymentJournalMoneyStatus.Failed;

  return h('div', { class: 'flex flex-col items-start gap-1' }, [
    h(
      'div',
      { class: ['font-medium whitespace-nowrap tabular-nums', failed && `${MUTED} line-through`] },
      signedAmount(row),
    ),
    status && superseded
      ? pill(getPaymentMoneyStatusTone(PaymentJournalMoneyStatus.Pending), 'Попытка не прошла')
      : status
        ? pill(getPaymentMoneyStatusTone(status), paymentJournalShortStatusLabel(row.kind, status))
        : null,
    failed && !superseded ? failureLine(paymentJournalFailureText(row)) : null,
    ...row.attention_reasons
      .filter((reason) => !SHOWN_ATTENTION.has(reason))
      .map((reason) => h('div', { class: 'text-xs text-(--warning)' }, paymentAttentionLabels[reason])),
  ]);
};
// Карта и наличные всегда идут через кассу локации, ссылка через Robokassa: канал подписываем только у оплаты без кассы.
const transferLine = (row: PaymentJournalRow): string | null => {
  const transfer = row.transfer_details;
  if (!transfer) return null;
  const order = `п/п № ${transfer.payment_order_number} от ${formatDate(transfer.received_on)}`;

  return transfer.invoice_number ? `Счёт № ${transfer.invoice_number}, ${order}` : order;
};
const methodCell = (row: PaymentJournalRow) => {
  const methods = paymentMethodsLabel(row.methods);
  const transfer = transferLine(row);
  if (transfer) return h('div', [h('div', methods || paymentProviderLabels[row.provider]), secondary(transfer)]);
  if (!methods) return paymentProviderLabels[row.provider];

  return row.provider === PaymentProvider.Manual
    ? h('div', [h('div', methods), secondary(paymentProviderLabels[row.provider])])
    : methods;
};
// Чек при сверке — это номер ФД. Без номера видно, где чек сейчас; касса локации подразумевается.
const receiptCell = (row: PaymentJournalRow) => {
  const status = row.receipt_status;
  if (!status) return secondary(row.provider === PaymentProvider.BankTransfer ? 'Без чека' : 'Нет');
  const issuer =
    row.receipt_issuer && row.receipt_issuer !== PaymentReceiptIssuer.LocationKkt
      ? paymentReceiptIssuerLabels[row.receipt_issuer]
      : null;
  const withoutNumber =
    status === PaymentReceiptStatus.Failed
      ? 'Не пробит'
      : status === PaymentReceiptStatus.Untracked
        ? (issuer ?? paymentReceiptStatusLabels[status])
        : paymentReceiptStatusLabels[status];
  const text = [
    row.receipt_settlement_method === ReceiptSettlementMethod.FullPrepayment ? 'Предоплата' : null,
    row.receipt_fd_number ? `ФД ${row.receipt_fd_number}` : withoutNumber,
    row.receipts_count > 1 ? `и ещё ${row.receipts_count - 1}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const tone = paymentJournalReceiptTone(row);
  const red = tone === 'error';

  return h('div', [
    tone === 'neutral'
      ? h('div', { class: `whitespace-nowrap ${row.receipt_fd_number ? '' : MUTED}` }, text)
      : pill(tone, text),
    row.receipt_fd_number && issuer ? secondary(issuer) : null,
    red ? failureLine(paymentJournalFailureText(row)) : null,
  ]);
};
// Возврат называет свой платёж: одинаковые суммы иначе не различить.
const refundNote = (row: PaymentJournalRow): string =>
  row.kind === PaymentJournalKind.Refund
    ? [
        row.payment_captured_at
          ? `Возврат платежа ${formatDateTime(inTimezone(row.payment_captured_at, row.timezone))}`
          : null,
        row.reason ? `Причина: ${row.reason}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
const sourceCell = (row: PaymentJournalRow) => {
  const wrapped = (text: string) => h('div', { class: `text-xs whitespace-normal ${MUTED}` }, text);
  const description = row.source_summary?.description;
  const note = refundNote(row);

  return h('div', { class: 'flex max-w-[360px] flex-col' }, [
    props.hideCustomer
      ? h('div', description ?? 'Без описания')
      : h('div', row.source_summary?.customer_name ?? 'Без клиента'),
    !props.hideCustomer && description ? wrapped(description) : null,
    note ? wrapped(note) : null,
  ]);
};
// Сверка идёт по смене, поэтому кассир нужен в каждой строке; у оплаты по ссылке сотрудника нет.
const employee = (row: PaymentJournalRow): string | null =>
  row.actor_title ?? (row.provider === PaymentProvider.Robokassa ? 'Онлайн' : null);

// Порядок банковской выписки: время, деньги, способ, чек, затем за что и где.
const columns = computed<ColumnDef<PaymentJournalRow>[]>(() => [
  { id: 'date', header: 'Дата', cell: ({ row }) => paymentJournalDateTime(row.original) },
  { id: 'amount', header: 'Сумма', cell: ({ row }) => amountCell(row.original) },
  { id: 'method', header: 'Способ', cell: ({ row }) => methodCell(row.original) },
  { id: 'receipt', header: 'Чек', cell: ({ row }) => receiptCell(row.original) },
  {
    id: 'source',
    header: props.hideCustomer ? 'Состав' : 'Клиент и состав',
    cell: ({ row }) => sourceCell(row.original),
  },
  props.hideLocation
    ? { id: 'employee', header: 'Сотрудник', cell: ({ row }) => employee(row.original) ?? '' }
    : {
        id: 'location',
        header: 'Локация',
        cell: ({ row }) => {
          const actor = employee(row.original);
          const badge = props.locationBadges?.get(row.original.location_id);
          const title = badge
            ? h('div', { class: 'flex items-center gap-1.5' }, [
                h(LocationCodeBadge, { code: badge.code, color: badge.color, size: 'xs' }),
                row.original.location_title,
              ])
            : h('div', row.original.location_title);

          return h('div', [title, actor ? secondary(actor) : null]);
        },
      },
]);
</script>

<template>
  <BaseTable
    :data="rows"
    :columns="columns"
    :loading="loading"
    :has-more="hasMore"
    :fetching-more="fetchingMore"
    :fixed-column-widths="false"
    server-side
    sticky
    :empty="empty"
    class="cursor-pointer"
    @fetch-more="emit('fetch-more')"
    @row-click="(row: PaymentJournalRow) => emit('row-click', row)"
  />
</template>
